import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useStore } from '../../store/store';

// Les bandes « + » (ajout de ligne sous une colonne, ajout de container entre
// deux blocs) longent le bas des cellules, là où la box sélectionnée porte sa
// poignée de redimensionnement du bas (classe posée par Resizer). La bande
// passait par-dessus : poignée impossible à attraper, et le « + » centré
// tombait pile dessus dès que la box était centrée.
// Plutôt que de faire gagner l'un ou l'autre en z-index, la bande laisse un
// trou à l'endroit de la poignée (clip-path, qui s'applique aussi au clic) et
// son « + » se décale à côté. Ailleurs, la bande reste cliquable.

// Marge autour de la poignée, et écart entre la poignée et le « + » décalé.
const HOLE_PAD = 2;
const PLUS_GAP = 6;
// Le « + » dépasse de sa bande (20 à 30px de haut pour une bande de 5 à
// 14px) : le contour extérieur du clip-path déborde pour ne pas le rogner.
const OUTER = 100;

// Géométrie pure, en px dans le repère non zoomé de la bande. `band` et
// `handle` sont des rects écran (getBoundingClientRect), `scale` le zoom
// appliqué au document. Null quand la poignée ne touche pas la bande.
export const resizeHandleClearance = ({ band, handle, scale = 1, plusSize = 0 }) => {
  const overlaps = handle.width > 0 &&
    handle.left < band.right && handle.right > band.left &&
    handle.top < band.bottom && handle.bottom > band.top;
  if (!overlaps) return null;

  const hole = {
    left: (handle.left - band.left) / scale - HOLE_PAD,
    right: (handle.right - band.left) / scale + HOLE_PAD,
    top: (handle.top - band.top) / scale - HOLE_PAD,
    bottom: (handle.bottom - band.top) / scale + HOLE_PAD,
  };
  const clipPath = `polygon(evenodd, ${-OUTER}px ${-OUTER}px, calc(100% + ${OUTER}px) ${-OUTER}px, ` +
    `calc(100% + ${OUTER}px) calc(100% + ${OUTER}px), ${-OUTER}px calc(100% + ${OUTER}px), ` +
    `${-OUTER}px ${-OUTER}px, ${hole.left}px ${hole.top}px, ${hole.right}px ${hole.top}px, ` +
    `${hole.right}px ${hole.bottom}px, ${hole.left}px ${hole.bottom}px, ${hole.left}px ${hole.top}px)`;

  // Le « + » est centré dans la bande : décalé à droite du trou, ou à gauche
  // s'il n'y a pas la place avant le bord de la bande.
  const width = band.width / scale;
  const center = width / 2;
  const half = plusSize / 2;
  let plusShift = 0;
  if (center + half > hole.left && center - half < hole.right) {
    plusShift = hole.right + PLUS_GAP + half <= width
      ? hole.right + PLUS_GAP + half - center
      : hole.left - PLUS_GAP - half - center;
  }
  return { clipPath, plusShift: Math.round(plusShift) };
};

const NONE = { clipPath: undefined, plusShift: 0 };

// Remesure à chaque sélection de box (la poignée apparaît ou change de box
// sans que la bande ne se remonte) et au relâché de la souris (une poignée
// tirée a bougé). La sélection est suivie par abonnement et non par
// sélecteur : les colonnes (mémoïsées) ne se re-rendent pas à chaque clic.
export const useResizeHandleClearance = ({ bandRef, active, plusSize }) => {
  const [clearance, setClearance] = useState(NONE);

  const measure = useCallback(() => {
    const node = bandRef.current;
    // Sans poignée à l'écran (rien de sélectionné), on ne lit pas la mise en
    // page : chaque bande d'ajout de container remesure à chaque clic.
    const handles = active && node ? document.querySelectorAll('.resizer-bottom-handle') : [];
    if (!handles.length) {
      setClearance(NONE);
      return;
    }
    const band = node.getBoundingClientRect();
    const scale = node.offsetWidth ? band.width / node.offsetWidth : 1;
    const next = Array.from(handles)
      .map((handle) => resizeHandleClearance({
        band, handle: handle.getBoundingClientRect(), scale, plusSize,
      }))
      .find(Boolean);
    setClearance((old) =>
      (next?.clipPath === old.clipPath && (next?.plusShift ?? 0) === old.plusShift ? old : next ?? NONE));
  }, [active, bandRef, plusSize]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    if (!active) return undefined;
    let frame;
    // Image suivante : la box sélectionnée a rendu sa poignée.
    const remeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const unsubscribe = useStore.subscribe(remeasure, (state) => state.selectedBoxId);
    window.addEventListener('mouseup', remeasure);
    // La poignée bouge aussi sans sélection ni souris (ligne supprimée, texte
    // qui grandit, annulation, hauteurs remesurées par la liste virtuelle) :
    // on remesure au moment où la souris arrive sur la bande, avant qu'elle
    // n'atteigne la poignée.
    const node = bandRef.current;
    node?.addEventListener('mouseover', measure);
    return () => {
      unsubscribe();
      window.removeEventListener('mouseup', remeasure);
      node?.removeEventListener('mouseover', measure);
      cancelAnimationFrame(frame);
    };
  }, [active, bandRef, measure]);

  return clearance;
};
